import React from 'react';
import {
  Heading,
  Text,
  Image,
  Box,
  OrderedList,
  UnorderedList,
  ListItem,
  Divider,
  AspectRatio,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Checkbox,
  Stack
} from '@chakra-ui/react';
import parse from 'html-react-parser';
import prefixUploadUrl from '@helpers/prefixUploadUrl';
import CookiePolicy from '@components/molecules/CookiePolicy';

const styles = {
  'h1, h2, h3, h4, h5, h6': {
    mb: 4
  },
  'p, ul, ol, figure, hr, .content-block': {
    mb: 4
  },
  a: {
    textDecoration: `underline`
  },
  figcaption: {
    fontSize: `sm`,
    mt: 2
  }
};

const headingSizes = {
  h1: `3xl`,
  h2: `2xl`,
  h3: `xl`,
  h4: `lg`,
  h5: `md`,
  h6: `sm`
};

const renderers = {
  // Headings
  header: ({ level, text }, props, blockTunes = null) => {
    const as = `h${level}`;
    const alignment = blockTunes?.anyTuneName?.alignment;
    return (
      <Heading as={as} size={headingSizes[as]} textAlign={alignment} {...props}>
        {parse(text)}
      </Heading>
    );
  },
  // Paragraphs
  paragraph: ({ text }, props, blockTunes = null) => {
    const alignment = blockTunes?.anyTuneName?.alignment;
    return (
      <Text textAlign={alignment} {...props}>
        {parse(text)}
      </Text>
    );
  },
  // Lists
  list: ({ style, items }, props) => {
    const listItems = items.map((text, i) => (
      <ListItem key={`${props.key}-${i + 1}`}>{parse(text)}</ListItem>
    ));
    return style === `ordered` ? (
      <OrderedList {...props}>{listItems}</OrderedList>
    ) : (
      <UnorderedList {...props}>{listItems}</UnorderedList>
    );
  },
  // Blockquotes
  quote: ({ text, caption }, props) => {
    return (
      <Box as="figure" {...props}>
        <Box as="blockquote">{parse(text)}</Box>
        {typeof caption === `string` && (
          <Box as="figcaption">{parse(caption)}</Box>
        )}
      </Box>
    );
  },
  // Images
  image: ({ file, caption }, props) => {
    return (
      <Box as="figure" {...props}>
        <Image src={prefixUploadUrl(file.url)} alt={caption} />
        {typeof caption === `string` && (
          <Box as="figcaption">{parse(caption)}</Box>
        )}
      </Box>
    );
  },
  // Embeds - YouTube and Vimeo
  // Other embed types have partial support and may not display correctly.
  // Full support will need to be added as and when its needed.
  embed: ({ service, embed, caption, width, height }, props) => {
    const isVideo = service === `youtube` || service === `vimeo`;
    return (
      <Box as="figure" {...props}>
        {isVideo ? (
          <AspectRatio ratio={16 / 9}>
            <iframe
              title={caption}
              src={embed}
              width={width}
              height={height}
              allowFullScreen
            />
          </AspectRatio>
        ) : (
          <iframe title={caption} src={embed} width={width} height={height} />
        )}
        {typeof caption === `string` && (
          <Box as="figcaption">{parse(caption)}</Box>
        )}
      </Box>
    );
  },
  // Tables
  table: ({ content, withHeadings }, props) => {
    const headings = withHeadings ? content.shift() : null;
    return (
      <TableContainer className="content-block" {...props}>
        <Table>
          {headings && (
            <Thead>
              <Tr>
                {headings.map((text, i) => (
                  <Th key={`${props.key}-head-${i + 1}`}>{parse(text)}</Th>
                ))}
              </Tr>
            </Thead>
          )}
          <Tbody>
            {content.map((cells, i) => {
              const key = `${props.key}-row-${i + 1}`;
              return (
                <Tr key={key}>
                  {cells.map((text, index) => (
                    <Td key={`${key}-${index + 1}`}>{parse(text)}</Td>
                  ))}
                </Tr>
              );
            })}
          </Tbody>
        </Table>
      </TableContainer>
    );
  },
  // Delimiters
  delimiter: (data, props) => {
    return <Divider {...props} />;
  },
  // Raw HTML
  raw: ({ html }, props) => {
    if (html === `<div id="ot-sdk-cookie-policy"></div>`) {
      return <CookiePolicy />;
    }
    return <Box {...props}>{parse(html)}</Box>;
  },
  // Code
  code: ({ code }, props) => {
    return (
      <Box as="pre" className="content-block" {...props}>
        {code}
      </Box>
    );
  },
  // Warnings
  warning: ({ title, message }, props) => {
    return (
      <Alert className="content-block" {...props}>
        <AlertIcon />
        <AlertTitle>{parse(title)}</AlertTitle>
        <AlertDescription>{parse(message)}</AlertDescription>
      </Alert>
    );
  },
  // Checklists
  checklist: ({ items }, props) => {
    return (
      <Stack className="content-block" {...props}>
        {items.map(({ text }, i) => (
          <Checkbox key={`${props.key}-${i + 1}`}>{parse(text)}</Checkbox>
        ))}
      </Stack>
    );
  }
};

const RichText = ({ data }) => {
  if (typeof data !== `string`) {
    return null;
  }

  // Parse JSON and remove blocks without a renderer.
  let json = false;

  if (data) {
    try {
      json = JSON.parse(data);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.log(`Error occured in RichText renderer: `, e);
    }
  }

  if (json === false) {
    return null;
  }

  const blocks = json.blocks.filter(
    ({ type }) => typeof renderers[type] === `function`
  );

  return (
    <Box sx={styles}>
      {blocks.map(({ id, type, data: blockData, tunes: blockTunes }) => {
        const props = { key: id };
        return renderers[type](blockData, props, blockTunes);
      })}
    </Box>
  );
};

export default RichText;
